/**
 * FactionsView — DM-facing tab for the per-campaign organizations.
 *
 * Phase WS-7 — sibling to NPCsView and LocationsView. Faction-specific
 * surface: `type` enum (guild / house / cult / government / order / company /
 * sect), `partyStanding` (allied / friendly / neutral / hostile / enemy /
 * unknown — color-codes the card's left border), `leaderNpcId` and
 * `memberNpcIds[]` referencing NPCs from the same series.
 *
 * The drawer's "Relationships with other factions" section consumes the
 * world_relationships table to render faction↔faction edges with an inline
 * add (relationship type Select + target faction Select). A real graph
 * visualisation (nodes + edges) is v2 polish — Mantine doesn't have a
 * native graph component and the chip-row form is already useful for
 * scanning who's at war with whom.
 *
 * This file is the smart wrapper: faction CRUD rides the shared
 * useWorldEntityMutations factory; it adds the faction↔faction relationship
 * mutations, the destructive-action confirm modals (delete, bulk
 * accept/delete), and the NPC + relationship reads the drawer needs. The
 * rendering surface lives in ./FactionsViewView, purely driven by props.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  acceptFaction,
  createFaction,
  createRelationship,
  deleteFaction,
  deleteRelationship,
  fetchFactions,
  fetchNpcs,
  fetchRelationships,
  type IFactionCreateBody,
  type IFactionEditBody,
  updateFaction,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { useWorldEntityMutations } from "../WorldEntityShell/useWorldEntityMutations"
import { FactionsViewView } from "./FactionsViewView"
import type { IRelationshipDraft } from "./FactionsViewView/interfaces"
import type { IProps } from "./interfaces"

export const FactionsView = ({ seriesId, initialEntityId }: IProps): React.JSX.Element => {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const [bulkBusy, setBulkBusy] = useState<boolean>(false)

  const factionsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldFactions(org, seriesId),
    queryFn: (client) => fetchFactions(client, seriesId),
  })

  // NPC roster for leader / member dropdowns. Re-uses the same query key as
  // NPCsView so React Query dedupes when both tabs have been visited.
  const npcsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldNpcs(org, seriesId),
    queryFn: (client) => fetchNpcs(client, seriesId),
  })

  // Per-series relationship list — we filter to faction-faction edges in the
  // drawer client-side. Backend supports filtering but a single fetch + memo
  // is cheaper than multiple per-faction round-trips when the DM is browsing.
  const relationshipsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldRelationships(org, seriesId),
    queryFn: (client) => fetchRelationships(client, seriesId),
  })

  const { createMutation, updateMutation, acceptMutation, deleteMutation } = useWorldEntityMutations<
    IFactionCreateBody,
    IFactionEditBody
  >({
    seriesId,
    queryKey: (org) => queryKeys.worldFactions(org, seriesId),
    create: createFaction,
    update: updateFaction,
    accept: acceptFaction,
    remove: deleteFaction,
    labels: {
      created: msg`Faction created`,
      saved: msg`Faction saved`,
      accepted: msg`Faction accepted`,
      deleted: msg`Faction deleted`,
    },
  })

  const createRelationshipMutation = useCompanyMutation({
    mutationFn: (
      client,
      { sourceId, targetId, relationshipType }: { sourceId: string; targetId: string; relationshipType: string },
    ) =>
      createRelationship(client, seriesId, {
        sourceKind: "faction",
        sourceId,
        targetKind: "faction",
        targetId,
        relationshipType,
      }),
    onSuccess: () => {
      notifications.show({ message: i18n._(msg`Relationship added`), color: "success" })
      queryClient.invalidateQueries({ queryKey: queryKeys.worldRelationships(orgId, seriesId) })
    },
    onError: (err) => notifications.show({ message: extractErrorMessage(err), color: "danger" }),
  })

  const deleteRelationshipMutation = useCompanyMutation({
    mutationFn: (client, relationshipId: string) => deleteRelationship(client, seriesId, relationshipId),
    onSuccess: () => {
      notifications.show({ message: i18n._(msg`Relationship removed`), color: "success" })
      queryClient.invalidateQueries({ queryKey: queryKeys.worldRelationships(orgId, seriesId) })
    },
    onError: (err) => notifications.show({ message: extractErrorMessage(err), color: "danger" }),
  })

  const handleCreateSubmit = useCallback(
    async (body: IFactionCreateBody): Promise<void> => {
      await createMutation.mutateAsync(body)
    },
    [createMutation],
  )

  const handleUpdateSubmit = useCallback(
    async (factionId: string, patch: IFactionEditBody): Promise<void> => {
      await updateMutation.mutateAsync({ id: factionId, patch })
    },
    [updateMutation],
  )

  const handleAcceptFaction = useCallback(
    (factionId: string): void => {
      acceptMutation.mutate(factionId)
    },
    [acceptMutation],
  )

  const handleDeleteFaction = useCallback(
    (factionId: string): void => {
      modals.openConfirmModal({
        title: i18n._(msg`Delete faction`),
        children: (
          <Text size="sm">
            <Trans>Are you sure you want to delete this faction? This cannot be undone.</Trans>
          </Text>
        ),
        labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => deleteMutation.mutate(factionId),
      })
    },
    [deleteMutation, i18n],
  )

  const handleAddRelationship = useCallback(
    (sourceId: string, draft: IRelationshipDraft): void => {
      createRelationshipMutation.mutate({
        sourceId,
        targetId: draft.targetId,
        relationshipType: draft.relationshipType,
      })
    },
    [createRelationshipMutation],
  )

  const handleDeleteRelationship = useCallback(
    (relationshipId: string): void => {
      deleteRelationshipMutation.mutate(relationshipId)
    },
    [deleteRelationshipMutation],
  )

  const allFactions = factionsQuery.data ?? []
  const pendingFactions = allFactions.filter((f) => f.pending)

  const handleAcceptAllPending = useCallback((): void => {
    if (!company || pendingFactions.length === 0) return
    modals.openConfirmModal({
      title: i18n._(msg`Accept all pending factions`),
      children: (
        <Text size="sm">
          <Trans>
            Mark all {pendingFactions.length} pending factions as accepted? You can still edit them afterwards.
          </Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Accept all`), cancel: i18n._(msg`Cancel`) },
      onConfirm: async () => {
        setBulkBusy(true)
        try {
          await Promise.all(pendingFactions.map((f) => acceptFaction(company, seriesId, f.id)))
          notifications.show({ message: i18n._(msg`Accepted ${pendingFactions.length} factions`), color: "success" })
          queryClient.invalidateQueries({ queryKey: queryKeys.worldFactions(orgId, seriesId) })
        } catch (err) {
          notifications.show({ message: extractErrorMessage(err), color: "danger" })
        } finally {
          setBulkBusy(false)
        }
      },
    })
  }, [company, pendingFactions, seriesId, queryClient, i18n, orgId])

  const handleDeleteAllPending = useCallback((): void => {
    if (!company || pendingFactions.length === 0) return
    modals.openConfirmModal({
      title: i18n._(msg`Delete all pending factions`),
      children: (
        <Text size="sm">
          <Trans>Permanently delete all {pendingFactions.length} pending factions? This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete all`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: async () => {
        setBulkBusy(true)
        try {
          await Promise.all(pendingFactions.map((f) => deleteFaction(company, seriesId, f.id)))
          notifications.show({
            message: i18n._(msg`Deleted ${pendingFactions.length} pending factions`),
            color: "success",
          })
          queryClient.invalidateQueries({ queryKey: queryKeys.worldFactions(orgId, seriesId) })
        } catch (err) {
          notifications.show({ message: extractErrorMessage(err), color: "danger" })
        } finally {
          setBulkBusy(false)
        }
      },
    })
  }, [company, pendingFactions, seriesId, queryClient, i18n, orgId])

  return (
    <FactionsViewView
      factions={allFactions}
      npcs={npcsQuery.data ?? []}
      relationships={relationshipsQuery.data ?? []}
      isLoading={factionsQuery.isLoading}
      initialSelectedId={initialEntityId}
      bulkBusy={bulkBusy}
      createPending={createMutation.isPending}
      updatePending={updateMutation.isPending}
      acceptPending={acceptMutation.isPending}
      deletePending={deleteMutation.isPending}
      createRelationshipPending={createRelationshipMutation.isPending}
      onCreateSubmit={handleCreateSubmit}
      onUpdateSubmit={handleUpdateSubmit}
      onAcceptFaction={handleAcceptFaction}
      onDeleteFaction={handleDeleteFaction}
      onAddRelationship={handleAddRelationship}
      onDeleteRelationship={handleDeleteRelationship}
      onAcceptAllPending={handleAcceptAllPending}
      onDeleteAllPending={handleDeleteAllPending}
    />
  )
}
